/**
 * Notification composition: the alert feed (useAlerts) shown as the stack
 * (AlertStack, bottom right), the attack, loss and starvation banners under
 * the timer, the edge alerts and the minimap pings, wiring go-to, dismiss and
 * Send troops actions.
 */

import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import type * as THREE from 'three';
import { type Cell, isStarving } from '@/game/sim';
import type { Notification } from '@/game/view/notifications';
import { RESOURCE_ICONS } from '@/game/view/resources';
import type { SendTarget } from '@/game/view/sendTroops';
import { AlertStack, DismissButton } from './AlertStack';
import { useSimFrame } from './app/simFrame';
import { EdgeAlerts } from './EdgeAlerts';
import { alertFocus, useAlerts } from './useAlerts';

interface NotificationsPanelProps {
  onFocus: (pos: Cell) => void;
  /** Select an own city and go there (idle-worker notifications). */
  onSelectCity: (id: string) => void;
  /** Select exactly these own units (a city's idle workers); the panel pans there. */
  onSelectUnits?: (ids: string[]) => void;
  /** Open the Send troops picker for a found rival or an attacker; omitted while spectating. */
  onSendTroops?: (target: SendTarget) => void;
  /** Open the Upgrades dock (the city era alert's Upgrades button); omitted while spectating. */
  onOpenUpgrades?: () => void;
  /** Sites the player cancelled (their removal isn't a loss); a live set, read each tick. */
  cancelled?: ReadonlySet<string>;
  /** The 3D view's camera, for the edge arrows pointing at urgent alerts off screen. */
  cameraRef?: RefObject<THREE.Camera | null>;
  /** The minimap, given the urgent alerts' tiles to ping; drawn into `minimapSlot`. */
  minimap?: (pings: Cell[]) => ReactNode;
  /** Where the minimap goes (the top of the left HUD column); nothing is drawn until it exists. */
  minimapSlot?: HTMLElement | null;
  /**
   * Where the alert stack goes instead of the default bottom-right column (a HUD
   * layout's notification slot, a height-bounded flex column); the panel still
   * owns the feed, banners, edge alerts and pings.
   */
  stackSlot?: HTMLElement | null;
}

/**
 * Alert stack (bottom-right: filter chips, per-kind mutes, tiered entries with
 * actions) plus banners under the timer: a loss (a city, a
 * barracks, other buildings or units) until acknowledged, and ongoing hits on the player's units or
 * buildings. Go ⌖ moves the camera
 * to an entry (an idle-workers one also selects its city, or its idle workers);
 * ✕ dismisses it, except an attack still being fought. Finding a rival and
 * attacks offer Send troops.
 */
export function NotificationsPanel({
  onFocus,
  onSelectCity,
  onSelectUnits,
  onSendTroops,
  onOpenUpgrades,
  cancelled,
  cameraRef,
  minimap,
  minimapSlot,
  stackSlot,
}: NotificationsPanelProps) {
  // The viewer's state (`view`) for what's shown; the real one for vision.
  const frame = useSimFrame();
  const { rules, playerId } = frame;
  const alerts = useAlerts(cancelled);
  const { ongoing, latestAttack, loss, closeLossBanner, urgent } = alerts;
  const state = frame.view;
  // Out of food for upkeep past the grace period: the army is losing hp.
  const starving = isStarving(state, rules, playerId);
  const focus = (n: Notification) => alertFocus(state, n, onFocus, onSelectCity);

  const stack = (
    <AlertStack
      alerts={alerts}
      onFocus={onFocus}
      onSelectCity={onSelectCity}
      onSelectUnits={onSelectUnits}
      onSendTroops={onSendTroops}
      onOpenUpgrades={onOpenUpgrades}
    />
  );

  return (
    <>
      {cameraRef && (
        <EdgeAlerts
          alerts={urgent}
          ongoing={ongoing.length > 0}
          cameraRef={cameraRef}
          onFocus={onFocus}
        />
      )}
      {/* Banners under the timer: a loss not yet acknowledged, starvation, then ongoing attacks. */}
      <div className="pointer-events-none absolute left-1/2 top-14 z-20 flex -translate-x-1/2 flex-col items-center gap-1">
        {loss && (
          <div
            role="alert"
            className="pill pointer-events-auto flex items-center border-danger bg-danger-surface text-sm font-semibold text-danger"
          >
            <button
              type="button"
              title="Go there (closes the banner)"
              onClick={() => {
                focus(loss.latest);
                closeLossBanner();
              }}
              className="py-1.5 pl-4 pr-2"
            >
              🔥 {loss.latest.text}
              {loss.others > 0 ? ` (+${loss.others} more)` : ''}{' '}
              <span className="font-normal text-fg-muted">— click to view</span>
            </button>
            <DismissButton onClick={closeLossBanner} />
          </div>
        )}
        {starving && (
          <div
            role="alert"
            title={`No food for army upkeep: each soldier loses ${rules.pacing.attrition} hp/s until you have food again`}
            className="pill animate-pulse border-danger/40 bg-danger-surface px-4 py-1.5 text-sm font-medium text-danger"
          >
            {RESOURCE_ICONS.food} Starving: soldiers losing hp
          </div>
        )}
        {latestAttack?.pos && (
          <div className="pill pointer-events-auto flex animate-pulse items-center border-danger/40 bg-danger-surface text-sm font-medium">
            <button
              type="button"
              onClick={() => latestAttack.pos && onFocus(latestAttack.pos)}
              className="py-1.5 pl-4 pr-2"
            >
              ⚔️{' '}
              {ongoing.length > 1
                ? `Under attack in ${ongoing.length} places`
                : `${latestAttack.text}${latestAttack.attackers ? ` · by ${latestAttack.attackers}` : ''}`}{' '}
              <span className="font-normal text-fg-muted">— click to view</span>
            </button>
            <DismissButton onClick={alerts.dismissOngoing} />
          </div>
        )}
      </div>
      {minimap && minimapSlot ? createPortal(minimap(urgent.map((a) => a.pos)), minimapSlot) : null}
      {stackSlot ? (
        createPortal(stack, stackSlot)
      ) : (
        // Bottom-right column: its list scrolls below a fixed header and above the pill, so the toggle never moves.
        <div className="pointer-events-none absolute bottom-3 right-3 z-10 flex max-h-[calc(100vh-17rem)] w-64 flex-col">
          {stack}
        </div>
      )}
    </>
  );
}
